import React from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { TradingCard, CardBack, SET_LOGO } from '../components/TradingCard';
import { ClompApi } from '../api/ClompApi';
import { playMysticReveal, playPackOpen, playSpecialReveal } from '../components/cardSounds';
import { ClompCoin, Coins } from '../components/ClompCoin';
import './CardsPage.css';

const api = new ClompApi();

const TYPE_FILTERS = [
    { id: 'all', label: 'All' },
    { id: 'character', label: 'Characters' },
    { id: 'world', label: 'Worlds' },
    { id: 'item', label: 'Items' }
];
const FINISH_FILTERS = [
    { id: 'all', label: 'Any finish' },
    { id: 'trimmed', label: 'Trimmed' },
    { id: 'mystic', label: 'Mystic' }
];
const RARITY_FILTERS = [
    { id: 'all', label: 'Any rarity' },
    { id: 'common', label: 'Common' },
    { id: 'rare', label: 'Rare' },
    { id: 'epic', label: 'Epic' },
    { id: 'legendary', label: 'Legendary' }
];
// The pack shakes for at least this long while it opens, even if the server answers sooner
const OPEN_ANIMATION_MS = 700;

function timeUntil(iso, now) {
    let minutes = Math.max(1, Math.ceil((new Date(iso).getTime() - now) / 60000));
    let hours = Math.floor(minutes / 60);
    if (hours >= 24) {
        return `${Math.floor(hours / 24)}d ${hours % 24}h`;
    }
    return hours > 0 ? `${hours}h ${minutes % 60}m` : `${minutes}m`;
}

export class CardsPage extends React.Component {

    state = {
        sets: [],
        cards: [],
        odds: null,
        loading: true,
        error: null,
        // When the page is showing one player's collection (/profile/<who>/cards) rather than the viewer's own
        // cards and packs: { id, username, isMine } once loaded
        owner: null,
        loggedIn: localStorage.getItem('token') !== null,
        loginExpired: false,
        collection: {},
        trimmed: {},
        mystic: {},
        // setID -> packs opened
        packsOpened: {},
        packs: null,
        // The player's Clomp Coins (null until known), and what a booster pack costs in them
        coins: null,
        packPrice: null,
        // the free packs there are ([{ id, name, every, rareSlots, trimChance, rarityWeights, rareSlotWeights }]) and which the player can open now
        // ({ packID: { available, nextAt } })
        freePackList: [],
        freePacks: {},
        tab: 'packs',
        phase: 'idle',
        // the pack being opened / just opened: a set's ID, or a free pack's
        packSet: null,
        revealed: [],
        setFilter: 'all',
        typeFilter: 'all',
        rarityFilter: 'all',
        finishFilter: 'all',
        selected: null,
        now: Date.now()
    };

    // The player whose collection this page is showing, from the address; undefined on the viewer's own cards page
    viewing() {
        return this.props.match && this.props.match.params.who;
    }

    componentDidMount() {
        this.load();


        // Keep the "next pack in" countdown fresh, and pick up a new pack when it arrives
        this.timer = setInterval(() => {
            let waits = [this.state.packs && this.state.packs.nextPackAt, ...Object.values(this.state.freePacks).map((pack) => pack.nextAt)];
            let arrived = waits.some((next) => next && new Date(next).getTime() <= Date.now());
            this.setState({ now: Date.now() });
            if (arrived && this.state.loggedIn && this.state.phase === 'idle') {
                this.loadMine();
            }
        }, 30000);

        window.addEventListener('keydown', this.onKeyDown);
    }

    componentDidUpdate(prevProps) {
        let before = prevProps.match && prevProps.match.params.who;
        if (before !== this.viewing()) {
            this.load();
        }
    }

    load() {
        let who = this.viewing();
        this.setState({ loading: true, error: null, owner: null, selected: null, tab: who ? 'collection' : 'packs' });
        api.cards.getCatalog().then((catalog) => {
            this.setState({ sets: catalog.sets || [], cards: catalog.cards, freePackList: catalog.freePacks || [], packPrice: catalog.packPrice === undefined ? null : catalog.packPrice, odds: { rarityWeights: catalog.rarityWeights, trimChance: catalog.trimChance, mysticChance: catalog.mysticChance, packSize: catalog.packSize } });
            if (who) {
                return this.loadPlayer(who);
            }
            return this.state.loggedIn ? this.loadMine() : null;
        }).catch((err) => {
            console.log('Error loading cards: ', err);
            this.setState({ error: "Couldn't load the cards right now. Please try again later." });
        }).finally(() => {
            this.setState({ loading: false });
        });
    }

    loadPlayer(who) {
        return api.profile.getCards(who).then((held) => {
            this.setState({
                owner: { id: held.id, username: held.username, isMine: held.isMine },
                collection: held.collection, trimmed: held.trimmed || {}, mystic: held.mystic || {}, packsOpened: held.packsOpened || {}
            });
        }).catch((err) => {
            if (err.response && err.response.status === 404) {
                this.setState({ error: "There's no player with that profile." });
                return;
            }
            throw err;
        });
    }

    componentWillUnmount() {
        clearInterval(this.timer);
        window.removeEventListener('keydown', this.onKeyDown);
    }

    // Space presses a button under an opened pack, wherever the focus is: "Reveal all" while cards are still
    // face-down, then "Other Packs". Never "Buy another": a key pressed once too often shouldn't spend coins.
    onKeyDown = (e) => {
        if (e.key !== ' ' || e.repeat || e.ctrlKey || e.altKey || e.metaKey) {
            return;
        }
        let { tab, phase, revealed, selected } = this.state;
        if (tab !== 'packs' || phase !== 'revealed' || selected) {
            return;
        }
        // stops the page scrolling, and a focused card or button from also taking the key
        e.preventDefault();
        if (revealed.every((r) => r.flipped)) {
            this.openAnother();
        } else {
            this.revealAll();
        }
    }

    // Buy and open another pack of the kind just opened, without going back to the packs first. "Other Packs"
    // afterwards still returns the page to where it was when the first of them was picked.
    buyAnother = () => {
        let scroll = this.scrollBeforePack;
        this.openPack(this.state.packSet);
        this.scrollBeforePack = scroll;
    }

    // Back to the packs, to pick the next one to open, with the page scrolled to where it was when the last pack
    // was picked (the opened pack's cards are a shorter page, so the browser will have moved it)
    openAnother = () => {
        let scroll = this.scrollBeforePack;
        this.setState({ phase: 'idle' }, () => {
            if (scroll !== undefined) {
                window.scrollTo(0, scroll);
            }
        });
    }

    loadMine() {
        return api.cards.getMine().then((mine) => {
            this.setState({ collection: mine.collection, trimmed: mine.trimmed || {}, mystic: mine.mystic || {}, packsOpened: mine.packsOpened || {}, packs: mine.packs, freePacks: mine.freePacks || {}, coins: mine.coins === undefined ? null : mine.coins });
        }).catch((err) => this.handleError(err));
    }

    handleError(err) {
        let status = err.response && err.response.status;
        if (status === 401) {
            this.setState({ loggedIn: false, loginExpired: true, phase: 'idle' });
        } else if (status === 409) {
            // no pack to open after all: show what the server says is left, coins included
            let left = err.response.data;
            this.setState({
                packs: left.packs || this.state.packs, freePacks: left.freePacks || this.state.freePacks, phase: 'idle',
                coins: left.coins === undefined ? this.state.coins : left.coins
            });
        } else {
            console.log('Card error: ', err);
            this.setState({ error: 'Something went wrong. Please try again.', phase: 'idle' });
        }
    }

    cardById(id) {
        return this.state.cards.find((card) => card.id === id);
    }

    // How many cards are in a card's set (each set is numbered on its own)
    setSize(card) {
        return this.state.cards.filter((other) => other.set === card.set).length;
    }

    // Open a booster pack from a set, or (with free set) the free pack with that ID
    openPack = (setID, free) => {
        this.scrollBeforePack = window.scrollY;
        playPackOpen();
        this.setState({ phase: 'opening', packSet: setID, revealed: [], error: null });
        let wait = new Promise((resolve) => setTimeout(resolve, OPEN_ANIMATION_MS));
        Promise.all([free ? api.cards.openFreePack(setID) : api.cards.openPack(setID), wait]).then(([result]) => {
            let collection = { ...this.state.collection };
            let trimmed = { ...this.state.trimmed };
            let mystic = { ...this.state.mystic };
            result.cards.forEach((card) => {
                collection[card.id] = (collection[card.id] || 0) + 1;
                if (card.trimmed) {
                    trimmed[card.id] = (trimmed[card.id] || 0) + 1;
                }
                if (card.mystic) {
                    mystic[card.id] = (mystic[card.id] || 0) + 1;
                }
            });
            // Only the first copy of a new card (or first Trimmed or Mystic copy) in the pack gets a badge
            let newLeft = [...result.newCards];
            let newTrimsLeft = [...(result.newTrims || [])];
            let newMysticsLeft = [...(result.newMystics || [])];
            let revealed = result.cards.map((card) => {
                let isNew = newLeft.includes(card.id);
                newLeft = newLeft.filter((n) => n !== card.id);
                let isNewTrim = card.trimmed && newTrimsLeft.includes(card.id);
                if (isNewTrim) {
                    newTrimsLeft = newTrimsLeft.filter((n) => n !== card.id);
                }
                let isNewMystic = Boolean(card.mystic) && newMysticsLeft.includes(card.id);
                if (isNewMystic) {
                    newMysticsLeft = newMysticsLeft.filter((n) => n !== card.id);
                }
                return { id: card.id, trimmed: card.trimmed, mystic: Boolean(card.mystic), isNew, isNewTrim, isNewMystic, flipped: false };
            });
            let packsOpened = { ...this.state.packsOpened, [setID]: (this.state.packsOpened[setID] || 0) + 1 };
            this.setState({ collection, trimmed, mystic, packsOpened, packs: result.packs || this.state.packs, freePacks: result.freePacks || this.state.freePacks, revealed, phase: 'revealed', coins: result.coins === undefined ? this.state.coins : result.coins });
        }).catch((err) => this.handleError(err));
    }

    // Legendary and Trimmed cards get their own sound when they turn over
    isSpecial(r) {
        let card = this.cardById(r.id);
        return r.trimmed || (card && card.rarity === 'legendary');
    }

    // The sound for cards turning over together: Mystic has its own, and wins over the Legendary and Trimmed chime
    playRevealSound(turning) {
        if (turning.some((r) => r.mystic)) {
            playMysticReveal();
        } else if (turning.some((r) => this.isSpecial(r))) {
            playSpecialReveal();
        }
    }

    flip(index) {
        let r = this.state.revealed[index];
        if (r && !r.flipped) {
            this.playRevealSound([r]);
        }
        this.setState({ revealed: this.state.revealed.map((r, i) => (i === index ? { ...r, flipped: true } : r)) });
    }

    revealAll = () => {
        this.playRevealSound(this.state.revealed.filter((r) => !r.flipped));
        this.setState({ revealed: this.state.revealed.map((r) => ({ ...r, flipped: true })) });
    }

    renderLoginPrompt(action) {
        return (
            <div className="cards-login">
                <p>
                    {this.state.loginExpired ? 'Your login has expired. ' : ''}
                    Log in to {action}.
                </p>
                <Link className="cards-btn cards-btn-primary" to="/login">Log in</Link>
            </div>
        );
    }

    // The lines under the free packs: their odds, and when new ones arrive
    freePackRules() {
        return (
            <>
                <p className="cards-pack-status">Free pack odds <PackOdds odds={this.state.odds} freePacks={this.state.freePackList} /></p>
                <p className="cards-pack-hint">
                    A new Daily Pack every day and a new Weekly Pack every Monday, at midnight UTC. Free packs draw from every game's cards.
                </p>
            </>
        );
    }

    renderPacks() {
        let { sets, packs, freePackList, freePacks, phase, packSet, revealed, now, coins, packPrice } = this.state;
        if (!this.state.loggedIn) {
            return (
                <section className="cards-pack-area">
                    {freePackList.length > 0 ?
                        <>
                            <h2 className="cards-pack-heading">Free packs</h2>
                            <div className="cards-pack-row" aria-hidden="true">
                                {freePackList.map((pack) => (
                                    <div key={pack.id} className={`cards-pack cards-pack-${pack.id}`}><FreePackFace pack={pack} /></div>
                                ))}
                            </div>
                            {this.freePackRules()}
                        </>
                        : null}
                    <h2 className="cards-pack-heading">Booster packs</h2>
                    <div className="cards-pack-row" aria-hidden="true">
                        {sets.map((set) => (
                            <div key={set.id} className={`cards-pack cards-pack-${set.id}`}><PackFace set={set} /></div>
                        ))}
                    </div>
                    <p className="cards-pack-status">
                        {this.state.packPrice ? <>A booster pack costs <Coins amount={this.state.packPrice} /> · </> : null}
                        Pack odds <PackOdds odds={this.state.odds} />
                    </p>
                    {this.renderLoginPrompt('open packs and start your collection')}
                </section>
            );
        }

        if (phase === 'revealed') {
            let allFlipped = revealed.every((r) => r.flipped);
            // a booster pack can be bought again there and then; a free pack has just been used up
            let booster = sets.some((set) => set.id === packSet);
            let price = packPrice || 0;
            return (
                <section className="cards-pack-area">
                    <ul className="cards-reveal">
                        {revealed.map((r, i) => (
                            <li key={i} className={`cards-flip${r.flipped ? ' is-flipped' : ''}`} style={{ animationDelay: `${i * 90}ms` }}>
                                <div className="cards-flip-inner">
                                    <div className="cards-flip-face cards-flip-back">
                                        <button type="button" className="cards-flip-btn" onClick={() => this.flip(i)} aria-label={`Reveal card ${i + 1}`}>
                                            <CardBack set={this.cardById(r.id).set} />
                                        </button>
                                    </div>
                                    <div className="cards-flip-face cards-flip-front">
                                        <TradingCard card={this.cardById(r.id)} total={this.setSize(this.cardById(r.id))} trimmed={r.trimmed} mystic={r.mystic} isNew={r.isNew} isNewTrim={r.isNewTrim} isNewMystic={r.isNewMystic} onClick={r.flipped ? () => this.setState({ selected: r.id }) : undefined} />
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                    <div className="cards-actions">
                        {allFlipped ?
                            <>
                                {booster ?
                                    <button type="button" className="cards-btn cards-btn-primary cards-btn-buy" onClick={this.buyAnother} disabled={coins === null || coins < price}
                                        aria-label={`Buy another pack for ${price} Clomp Coins`} title={coins !== null && coins < price ? `You have ${coins.toLocaleString()} Clomp Coins` : undefined}>
                                        Buy another <Coins amount={price} />
                                    </button>
                                    : null}
                                <button type="button" className={`cards-btn ${booster ? 'cards-btn-ghost' : 'cards-btn-primary'}`} onClick={this.openAnother}>Other Packs</button>
                                <button type="button" className="cards-btn cards-btn-ghost" onClick={() => this.setState({ tab: 'collection', phase: 'idle' })}>View collection</button>
                            </>
                            :
                            <button type="button" className="cards-btn cards-btn-ghost" onClick={this.revealAll}>Reveal all</button>
                        }
                    </div>
                </section>
            );
        }

        let unlimited = Boolean(packs && packs.unlimited);
        let available = unlimited ? Infinity : packs ? packs.available : 0;
        // a booster pack is bought with Clomp Coins as it is opened
        let price = packPrice || 0;
        let canAfford = coins !== null && coins >= price;
        let short = coins === null ? 0 : Math.max(0, price - coins);
        return (
            <section className="cards-pack-area">
                {freePackList.length > 0 ?
                    <>
                        <h2 className="cards-pack-heading">Free packs</h2>
                        <div className="cards-pack-row">
                            {freePackList.map((pack) => {
                                let mine = freePacks[pack.id];
                                let ready = Boolean(mine && mine.available);
                                return (
                                    <div key={pack.id} className="cards-free-pack">
                                        <button type="button" className={`cards-pack cards-pack-${pack.id}${phase === 'opening' && packSet === pack.id ? ' is-opening' : ''}`}
                                            onClick={() => this.openPack(pack.id, true)} disabled={!ready || phase === 'opening'} aria-label={`Open your ${pack.name}`}>
                                            <FreePackFace pack={pack} />
                                        </button>
                                        <span className={`cards-pack-timer${ready ? ' is-ready' : ''}`}>
                                            {ready ? 'Ready to open' : mine && mine.nextAt ? `Next in ${timeUntil(mine.nextAt, now)}` : '\u00a0'}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                        {this.freePackRules()}
                    </>
                    : null}
                <h2 className="cards-pack-heading">Booster packs</h2>
                <div className="cards-pack-row">
                    {sets.map((set) => (
                        <button key={set.id} type="button" className={`cards-pack cards-pack-${set.id}${phase === 'opening' && packSet === set.id ? ' is-opening' : ''}`}
                            onClick={() => this.openPack(set.id)} disabled={available < 1 || !canAfford || phase === 'opening'}
                            aria-label={`Open a ${set.name} pack for ${price} Clomp Coins`}>
                            <PackFace set={set} />
                        </button>
                    ))}
                </div>
                <p className="cards-pack-status cards-coins" role="status">
                    {coins === null ? 'Counting your Clomp Coins...' :
                        <>
                            You have <strong><Coins amount={coins} /></strong> · a pack costs <strong><Coins amount={price} /></strong>
                        </>}
                    {unlimited || !packs ? null :
                        <>
                            {' '}· <strong>{available} of {packs.max}</strong> packs ready
                            {packs.nextPackAt ? <> · next pack in {timeUntil(packs.nextPackAt, now)}</> : null}
                        </>}
                    <PackOdds odds={this.state.odds} />
                </p>
                <p className="cards-pack-hint">
                    {phase === 'opening' ? 'Opening...'
                        : available < 1 ? 'No packs right now. A new one arrives every few hours.'
                        : canAfford ? 'Click a pack to buy and open it. Each booster pack has 5 cards from its own game, and the last one is always Rare or better. Any card can come Trimmed in gold, and a rare few come Mystic.'
                        : coins === null ? '\u00a0'
                        : `You need ${short.toLocaleString()} more Clomp ${short === 1 ? 'Coin' : 'Coins'} for a booster pack.`}
                </p>
                <p className="cards-pack-hint cards-coins-how">
                    <ClompCoin /> Earn Clomp Coins by earning achievements and by playing <Link to="/games">the games</Link>: Solarflare pays the most for your time, then Space Jump, then Clompy Clicker.
                </p>
            </section>
        );
    }

    renderCollection() {
        let { sets, cards, collection, trimmed, mystic, packsOpened, setFilter, typeFilter, rarityFilter, finishFilter } = this.state;
        let setFilters = [{ id: 'all', label: 'All games' }, ...sets.map((set) => ({ id: set.id, label: set.name }))];
        let owned = cards.filter((card) => collection[card.id]).length;
        let ownedTrimmed = cards.filter((card) => trimmed[card.id]).length;
        let ownedMystic = cards.filter((card) => mystic[card.id]).length;
        let owns = { trimmed: trimmed, mystic: mystic };
        // every pack (free ones included), or just the chosen game's booster packs
        let filteredSet = sets.find((set) => set.id === setFilter);
        let opened = filteredSet ? (packsOpened[filteredSet.id] || 0) : Object.values(packsOpened).reduce((sum, n) => sum + n, 0);
        let shown = cards.filter((card) => (setFilter === 'all' || card.set === setFilter)
            && (typeFilter === 'all' || card.type === typeFilter)
            && (rarityFilter === 'all' || card.rarity === rarityFilter)
            && (finishFilter === 'all' || owns[finishFilter][card.id]));

        return (
            <section>
                {!this.state.loggedIn && !this.viewing() ? this.renderLoginPrompt('see your collection') : null}
                <div className="cards-progress">
                    <div className="cards-progress-label">
                        <strong>{owned} / {cards.length}</strong> cards collected
                        {ownedTrimmed > 0 ? <> · <span className="cards-trim-count">{ownedTrimmed} Trimmed</span></> : null}
                        {ownedMystic > 0 ? <> · <span className="cards-mystic-count">{ownedMystic} Mystic</span></> : null}
                    </div>
                    <div className="cards-progress-bar" role="progressbar" aria-valuemin="0" aria-valuemax={cards.length} aria-valuenow={owned}>
                        <div style={{ width: `${cards.length ? (owned / cards.length) * 100 : 0}%` }}></div>
                    </div>
                    {this.state.loggedIn || this.viewing() ?
                        <div className="cards-packs-opened">
                            <strong>{opened.toLocaleString()}</strong> {filteredSet ? `${filteredSet.name} ` : ''}{opened === 1 ? 'pack' : 'packs'} opened
                        </div>
                        : null}
                </div>
                <div className="cards-filters">
                    {sets.length > 1 ? <FilterGroup label="Game" options={setFilters} value={setFilter} onChange={(id) => this.setState({ setFilter: id })} /> : null}
                    <FilterGroup label="Type" options={TYPE_FILTERS} value={typeFilter} onChange={(id) => this.setState({ typeFilter: id })} />
                    <FilterGroup label="Rarity" options={RARITY_FILTERS} value={rarityFilter} onChange={(id) => this.setState({ rarityFilter: id })} />
                    <FilterGroup label="Finish" options={FINISH_FILTERS} value={finishFilter} onChange={(id) => this.setState({ finishFilter: id })} />
                </div>
                {shown.length === 0 ?
                    <p className="cards-status">{finishFilter === 'trimmed' && ownedTrimmed === 0 ? 'No Trimmed cards yet. Every card you pull has a small chance to come Trimmed.'
                        : finishFilter === 'mystic' && ownedMystic === 0 ? 'No Mystic cards yet. A Trimmed card has a small chance to come Mystic instead.'
                        : 'No cards match those filters.'}</p>
                    :
                    <ul className="cards-grid">
                        {shown.map((card) => (
                            <li key={card.id}>
                                {collection[card.id] ?
                                    <TradingCard card={card} total={this.setSize(card)} count={collection[card.id]} trimmed={Boolean(trimmed[card.id])} mystic={finishFilter !== 'trimmed' && Boolean(mystic[card.id])} onClick={() => this.setState({ selected: card.id })} />
                                    :
                                    <TradingCard card={card} total={this.setSize(card)} locked />
                                }
                            </li>
                        ))}
                    </ul>
                }
            </section>
        );
    }

    renderSelected() {
        let card = this.cardById(this.state.selected);
        if (!card) {
            return null;
        }
        let close = () => this.setState({ selected: null });
        let copies = this.state.collection[card.id] || 0;
        let trimmedCopies = this.state.trimmed[card.id] || 0;
        let mysticCopies = this.state.mystic[card.id] || 0;
        return (
            <div className="cards-modal" role="dialog" aria-modal="true" aria-label={card.name} onClick={close} onKeyDown={(e) => e.key === 'Escape' && close()}>
                <div className="cards-modal-body" onClick={(e) => e.stopPropagation()}>
                    <TradingCard card={card} total={this.setSize(card)} count={copies} trimmed={trimmedCopies > 0} mystic={mysticCopies > 0} detailed />
                    <p className="cards-modal-owned">
                        {this.state.owner && !this.state.owner.isMine ? `${this.state.owner.username} has` : 'You have'} {copies} {copies === 1 ? 'copy' : 'copies'}{trimmedCopies > 0 ? `, ${trimmedCopies} Trimmed` : ''}{mysticCopies > 0 ? `, ${mysticCopies} Mystic` : ''}.
                    </p>
                    <button type="button" className="cards-btn cards-btn-ghost" onClick={close} autoFocus>Close</button>
                </div>
            </div>
        );
    }

    render() {
        let { loading, error, tab, cards, collection, owner } = this.state;
        let owned = cards.filter((card) => collection[card.id]).length;
        let viewing = this.viewing();

        return (
            <>
                <Navbar history={this.props.history}></Navbar>
                <div className="cards-page">
                    {viewing ?
                        <div className="cards-page-header cards-page-header-player">
                            <p className="cards-eyebrow">Card collection</p>
                            <h1>{owner ? owner.username : 'Trading Cards'}</h1>
                            <p className="cards-intro">
                                {owner ? <Link to={`/profile/${encodeURIComponent(owner.username)}`}>Back to {owner.isMine ? 'your' : `${owner.username}'s`} profile</Link> : null}
                                {owner && owner.isMine ? <> · <Link to="/cards">Open packs</Link></> : null}
                            </p>
                        </div>
                        :
                        <div className="cards-page-header">
                            <p className="cards-eyebrow">Clomp games</p>
                            <h1>Trading Cards</h1>
                            <p className="cards-intro">Collect the characters, worlds and items of Solarflare, Space Jump and Clompy Clicker. Open packs to find new cards.</p>
                        </div>
                    }

                    {viewing ? null :
                        <div className="cards-tabs" role="tablist">
                            <button type="button" role="tab" aria-selected={tab === 'packs'} className={tab === 'packs' ? 'is-active' : ''} onClick={() => this.setState({ tab: 'packs' })}>Open packs</button>
                            <button type="button" role="tab" aria-selected={tab === 'collection'} className={tab === 'collection' ? 'is-active' : ''} onClick={() => this.setState({ tab: 'collection' })}>
                                Collection{cards.length ? ` (${owned}/${cards.length})` : ''}
                            </button>
                        </div>
                    }

                    {error ? <p className="cards-status cards-error">{error}</p> : null}
                    {loading ?
                        <p className="cards-status">Loading cards...</p>
                        :
                        viewing ? (owner && cards.length > 0 ? this.renderCollection() : null)
                        : cards.length > 0 ? (tab === 'packs' ? this.renderPacks() : this.renderCollection()) : null
                    }
                </div>
                {this.renderSelected()}
            </>
        );
    }

}

function PackFace({ set }) {
    return (
        <span className="cards-pack-face">
            <span className="cards-pack-brand">{set.name}</span>
            <img src={SET_LOGO[set.id] || SET_LOGO.solarflare} alt="" />
            <span className="cards-pack-label">Booster Pack</span>
            <span className="cards-pack-count">5 cards</span>
        </span>
    );
}

// A free pack's wrapper: the Clomp logo rather than one game's, since its cards come from all of them
function FreePackFace({ pack }) {
    return (
        <span className="cards-pack-face cards-pack-face-free">
            <span className="cards-pack-brand">All games</span>
            <img src="/img/cards/clomp-logo.png" alt="" />
            <span className="cards-pack-label">{pack.name}</span>
            <span className="cards-pack-count">5 cards · free</span>
        </span>
    );
}

const RARITY_ORDER = ['common', 'rare', 'epic', 'legendary'];

// 0.55 -> "55%", 0.6667 -> "66.7%"
function percent(chance) {
    return `${Math.round(chance * 1000) / 10}%`;
}

// The (i) next to the packs: shows the rarity and Trimmed odds on hover or keyboard focus, and toggles on tap.
// With freePacks (the list of free packs) it shows their odds rather than the booster packs'.
function PackOdds({ odds, freePacks }) {
    let [open, setOpen] = React.useState(false);
    // Escape hides the panel even while the (i) keeps focus or the pointer stays over it, until it's shown again
    let [dismissed, setDismissed] = React.useState(false);
    if (!odds || !odds.rarityWeights) {
        return null;
    }
    let free = freePacks && freePacks.length > 0;
    let weights = (free && freePacks[0].rarityWeights) || odds.rarityWeights;
    // the rare-or-better slots: a free pack's own odds, or a booster's normal ones with Common taken out
    let rareWeights = (free && freePacks[0].rareSlotWeights) || weights;
    let rareOrBetter = RARITY_ORDER.slice(1).reduce((sum, r) => sum + rareWeights[r], 0);
    let packSize = odds.packSize || 5;
    // how many cards at the end of the pack are always Rare or better
    let rareSlots = free ? freePacks[0].rareSlots : 1;
    let trims = free ? freePacks.map((pack) => ({ label: `${pack.name} Trimmed`, chance: pack.trimChance })) : [{ label: 'Trimmed', chance: odds.trimChance || 0 }];
    trims = trims.filter((trim) => trim.chance > 0);
    let mystic = odds.mysticChance || 0;
    let popID = free ? 'cards-odds-pop-free' : 'cards-odds-pop';
    let firstRare = packSize - rareSlots + 1;

    return (
        <span
            className={`cards-odds${open ? ' is-open' : ''}${dismissed ? ' is-dismissed' : ''}`}
            onMouseEnter={() => setDismissed(false)}
            onMouseLeave={() => setOpen(false)}
            onKeyDown={(e) => {
                if (e.key === 'Escape') {
                    setOpen(false);
                    setDismissed(true);
                }
            }}
        >
            <button type="button" className="cards-odds-btn" aria-label={free ? 'Free pack odds' : 'Pack odds'} aria-describedby={popID} aria-expanded={open}
                onFocus={() => setDismissed(false)} onBlur={() => setOpen(false)}
                onClick={() => { setDismissed(false); setOpen(!open); }}>i</button>
            <span className="cards-odds-pop" id={popID} role="tooltip">
                <span className="cards-odds-title">{free ? 'Free pack odds' : 'Pack odds'}</span>
                <span className="cards-odds-table">
                    <span className="cards-odds-head"></span>
                    <span className="cards-odds-head">Cards 1–{firstRare - 1}</span>
                    <span className="cards-odds-head">{rareSlots > 1 ? `Cards ${firstRare}–${packSize}` : `Card ${packSize}`}</span>
                    {RARITY_ORDER.map((r) => (
                        <React.Fragment key={r}>
                            <span className={`cards-odds-rarity cards-odds-${r}`}>{r[0].toUpperCase() + r.slice(1)}</span>
                            <span>{percent(weights[r])}</span>
                            <span>{r === 'common' ? '–' : percent(rareWeights[r] / rareOrBetter)}</span>
                        </React.Fragment>
                    ))}
                </span>
                {trims.map((trim) => (
                    <span key={trim.label} className="cards-odds-trim">
                        <strong>{trim.label}:</strong> 1 in {Math.round(1 / trim.chance)} cards ({percent(trim.chance)}), any rarity.
                        About {percent(1 - Math.pow(1 - trim.chance, packSize))} of packs have at least one.
                    </span>
                ))}
                {trims.length > 0 && mystic > 0 ?
                    <span className="cards-odds-trim cards-odds-mystic">
                        <strong>Mystic:</strong> 1 in {Math.round(1 / mystic)} Trimmed cards comes Mystic instead{trims.length === 1 ?
                            `, about 1 in ${Math.round(1 / (trims[0].chance * mystic)).toLocaleString()} cards.` : '.'}
                    </span>
                    : null}
            </span>
        </span>
    );
}

function FilterGroup({ label, options, value, onChange }) {
    return (
        <div className="cards-filter-group" role="group" aria-label={label}>
            {options.map((option) => (
                <button key={option.id} type="button" className={value === option.id ? 'is-active' : ''} aria-pressed={value === option.id} onClick={() => onChange(option.id)}>
                    {option.label}
                </button>
            ))}
        </div>
    );
}
